@if (currentUser$ | async; as user) {
  <div class="header">
    <div class="tabs">
      <div [class.active]="currentTab === 'MEETING'" (click)="currentTab = 'MEETING'" class="clickable tab" translate="COMMON.CALL"></div>
      <div
        [class.active]="currentTab === 'AGENDA'"
        (click)="handleAgendaTabClick()"
        class="tab clickable"
        [matTooltip]="isAuthorizedToManageDocuments ? null : ('CALLS.CALL_INFO.DOCUMENT_UPLOAD.ONLY_OWNERS_CAN_UPLOAD' | translate)"
      >
        <span translate="CALLS.TEMPLATE"></span>
      </div>
      <div
        [class.active]="currentTab === 'DOCUMENTS'"
        class="tab documents-tab"
        [class.clickable]="call.userUploadedResources.length > 0 || isAuthorizedToManageDocuments"
        [matTooltip]="isAuthorizedToManageDocuments ? null : ('CALLS.CALL_INFO.DOCUMENT_UPLOAD.ONLY_OWNERS_CAN_UPLOAD' | translate)"
      >
        @if (call.userUploadedResources.length > 0) {
          <span class="documents-tab-trigger">
            <span (click)="handleDocumentsTabClick()" class="tab-text clickable">{{ papersTabLabel }}</span>
            <mat-icon [matMenuTriggerFor]="documentsMenu" class="dropdown-icon clickable" (click)="$event.stopPropagation()">arrow_drop_down</mat-icon>
          </span>
          <mat-menu #documentsMenu="matMenu" class="documents-menu">
            @for (resource of call.userUploadedResources; track resource.id) {
              @if (resource.downloadUrl !== null) {
                <button mat-menu-item (click)="onDocumentSelectionChange(resource.id)">
                  <span class="mask-file-names">{{ resource.fileName }}</span>
                  @if (resource.uploadedByIngestBoardPackJob) {
                    <span class="board-pack-badge">{{ 'COMMON.FROM_BOARD_PACK_BADGE' | translate }}</span>
                  }
                </button>
              }
            }
          </mat-menu>
        } @else {
          <span (click)="handleDocumentsTabClick()" class="clickable" translate="CALLS.CALL_INFO.DOCUMENT_UPLOAD.PAPERS"></span>
        }
      </div>
    </div>
    <div class="buttons">
      @if (isReprocessOutputsButtonVisible && canEdit) {
        <span [matTooltip]="call.isMinutesRegenerationInFlight ? ('CALLS.REPROCESS_OUTPUTS.IN_FLIGHT_TOOLTIP' | translate) : ''" [matTooltipShowDelay]="0">
          <button class="regenerate-button" [disabled]="!allowReprocessingOutputs" (click)="reprocessLlm()">
            <mat-icon>cached</mat-icon>
            {{ (call.hasMinutes ? 'CALLS.REPROCESS_OUTPUTS.TITLE' : 'CALLS.REPROCESS_OUTPUTS.TITLE_FIRST_TIME') | translate }}
          </button>
        </span>
      }
    </div>
  </div>
  <div class="meeting-container panel" [class.hidden]="!displayMeeting">
    <as-split direction="vertical" [gutterSize]="0" [unit]="shouldMaximizeTranscript(call) ? 'pixel' : 'percent'">
      <as-split-area [size]="shouldMaximizeTranscript(call) ? 48 : 40" [lockSize]="true">
        <div class="media-container ph-no-capture">
          @if (call.isMediaReady) {
            <app-media-player
              [call]="call"
              [shouldNavigateTranscriptBasedOnMediaTime]="shouldNavigateTranscriptBasedOnMediaTime"
              [labelingModeEnabled]="((speakerLabelingModeEnabled$ | async) ?? false) && canEdit"
            ></app-media-player>
          }
        </div>
      </as-split-area>
      <as-split-area [size]="shouldMaximizeTranscript(call) ? '*' : 60" [lockSize]="true">
        <div class="bottom-container">
          <div class="content">
            @if (call.isTranscriptReady) {
              <div class="speaker-x-ray-container">
                <app-speaker-x-ray
                  [call]="call"
                  [canEdit]="canEdit"
                  [canReprocessOutputs]="allowReprocessingOutputs"
                  [currentProgress]="currentProgress"
                  [activeSpeakerId]="activeSpeakerId"
                  (selectTime)="selectTimestamp($event)"
                  [showXRay]="(shouldShowSpeakers$ | async) ?? false"
                  [labelingModeEnabled]="((speakerLabelingModeEnabled$ | async) ?? false) && canEdit"
                ></app-speaker-x-ray>
              </div>
              <div class="transcript-container">
                @let isSupportUser = (isSupportUser$ | async) ?? false;
                <div class="transcript-actions">
                  @if (isSupportUser && call.recordingClockAnchors !== null) {
                    <button
                      class="toggle-clock-time"
                      (click)="toggleTranscriptClockTime()"
                      mat-icon-button
                      [class.active]="showTranscriptClockTime"
                      [attr.aria-pressed]="showTranscriptClockTime"
                      [attr.aria-label]="(showTranscriptClockTime ? 'CALLS.HIDE_TRANSCRIPT_CLOCK_TIME' : 'CALLS.SHOW_TRANSCRIPT_CLOCK_TIME') | translate"
                      [matTooltip]="(showTranscriptClockTime ? 'CALLS.HIDE_TRANSCRIPT_CLOCK_TIME' : 'CALLS.SHOW_TRANSCRIPT_CLOCK_TIME') | translate"
                    >
                      <mat-icon>schedule</mat-icon>
                    </button>
                  }
                  @if (call.haveSpeakersBeenUpdated && canEdit) {
                    @if (resetSpeakersMutation.result$ | async; as resetSpeakersResult) {
                      @if (resetSpeakersResult.isPending) {
                        <app-spinner size="medium"></app-spinner>
                      } @else {
                        <button (click)="resetSpeakers(call)" mat-icon-button [matTooltip]="'CALLS.RESET_SPEAKERS' | translate">
                          <mat-icon>refresh</mat-icon>
                        </button>
                      }
                    }
                  }
                </div>
                <div class="scrolling-container" (scrollend)="transcriptScroll$.next()">
                  <app-transcript
                    [canEdit]="canEdit"
                    [transcript]="call.transcript"
                    [call]="call"
                    [isSensitiveModeEnabled]="isSensitiveModeEnabled"
                    [showClockTime]="isSupportUser && showTranscriptClockTime"
                    (selectSentence)="selectSentence($event)"
                  ></app-transcript>
                </div>
                @if (!shouldNavigateTranscriptBasedOnMediaTime) {
                  <div class="jump-to-transcription-container">
                    <div class="button clickable" translate="CALLS.SYNC_TO_VIDEO" (click)="jumpToTranscript()"></div>
                  </div>
                }
              </div>
            } @else {
              <div class="centered-content">
                @if (call.isSpeechMissing) {
                  <h1>{{ 'CALLS.NO_SPEECH_MESSAGE' | translate }}</h1>
                } @else {
                  <h1>{{ 'CALLS.TRANSCRIPT_PROCESSING_MESSAGE' | translate }}</h1>
                }
              </div>
            }
          </div>
        </div>
      </as-split-area>
    </as-split>
  </div>
  @if (call.userUploadedResources.length > 0 && selectedDocumentPdfBlob !== null) {
    <div class="document-display-container embed-container" [class.hidden]="!displayDocuments">
      <ngx-extended-pdf-viewer
        #pdfViewer
        [src]="selectedDocumentPdfBlob"
        [(page)]="currentPdfPage"
        (pagesLoaded)="onPdfPagesLoaded()"
        [zoom]="'page-fit'"
        [activeSidebarView]="2"
        [textLayer]="true"
        [handTool]="false"
        [showToolbar]="true"
        [showSidebarButton]="true"
        [showFindButton]="true"
        [showPagingButtons]="true"
        [showZoomButtons]="true"
        [showPresentationModeButton]="false"
        [showOpenFileButton]="false"
        [showPrintButton]="false"
        [showDownloadButton]="false"
        [showSecondaryToolbarButton]="true"
        [showRotateCwButton]="true"
        [showSpreadButton]="true"
        [showHandToolButton]="true"
        [showPropertiesButton]="false"
        [contextMenuAllowed]="true"
        [useInlineScripts]="false"
        [showFirstAndLastPageButtons]="false"
        backgroundColor="#1a1a2e"
      ></ngx-extended-pdf-viewer>
    </div>
  }
  <div class="document-display-container panel" [class.hidden]="!displayAgenda">
    @if (isPopulatingTemplate) {
      <h1 class="centered">{{ 'TEMPLATES.WILL_BE_DISPLAYED_HERE' | translate }}</h1>
    } @else {
      @if (templateItems$ | async; as templateItemsResult) {
        @if (templateItemsResult.data) {
          @if (shouldShowTemplateSourcePicker(templateItemsResult.data.length)) {
            <div class="template-source-container">
              <div class="template-source-card">
                <div class="source-header">
                  <p>{{ 'CALL_SETUP.TEMPLATE_SOURCE.DESCRIPTION' | translate }}</p>
                </div>

                <app-template-source-picker
                  #templateSourcePicker
                  [canEdit]="canEdit"
                  [status]="agendaUploadStatus"
                  [failureMessageKey]="agendaFailureMessageKey"
                  [maxAgendaSize]="maxAgendaSize"
                  [uploadedFileName]="uploadedAgendaFileName"
                  [templateSourceType]="call.templateSourceType"
                  [requiresConfirmation]="shouldConfirmTemplateReplace"
                  (agendaFileChange)="onAgendaFileChange($event)"
                  (confirmationRequested)="onUploadConfirmationRequested()"
                  (createNewClicked)="createBlankTemplate()"
                  (loadSavedClicked)="onLoadSavedTemplate()"
                />
              </div>
            </div>
          } @else {
            <app-template-editor
              #templateEditor
              [readonly]="!canEdit"
              [showProcessingStatus]="showTemplateEditorProcessingStatus"
              [templateItems]="templateItemsResult.data"
              [availableResources]="call.userUploadedResources"
              [allowFileUpload]="isAuthorizedToManageDocuments"
              [allowLoadTemplate]="true"
              [attendeeNames]="call.attendeeNames"
              [restrictedUsers]="restrictedUsers"
              [systemGuidanceEditingEnabled]="call.allowSystemGuidanceEditing"
              [usePartialMinutesRegeneration]="call.usePartialMinutesRegeneration"
              [defaultLevelOfDetail]="call.minutesVerboseness!"
              [defaultAnonymization]="call.minutesAnonymization!"
              [processingTemplateItemIds]="call.minutesGenerationTargetTemplateItemIds ?? []"
              (templateSaved)="saveTemplate($event)"
              (fileUploadRequested)="handleFileUpload($event, call.id)"
              (loadTemplateRequested)="onLoadSavedTemplate()"
              (saveAsRequested)="onSaveAsTemplate($event)"
              (templateItemReprocessRequested)="reprocessTemplateItem($event)"
            ></app-template-editor>
          }
        }
      }
    }
  </div>
}
